/**
 * VertexHandle Component
 *
 * A draggable vertex handle for polygon editing in the map editor.
 * Rendered as a circle at each polygon vertex that can be dragged to reposition.
 *
 * Features:
 * - Visual indicator at polygon vertices
 * - Draggable for repositioning
 * - Hover state styling
 * - Touch-friendly sizing for tablet use
 *
 * @example
 * ```tsx
 * <VertexHandle
 *   x={100}
 *   y={100}
 *   index={0}
 *   isActive={activeVertexIndex === 0}
 *   onDragStart={() => startDraggingVertex(0)}
 *   onDragMove={(pos) => handleDragMove(0, pos)}
 *   onDragEnd={() => stopDraggingVertex()}
 * />
 * ```
 */

import { useState, useCallback } from 'react';
import { Circle } from 'react-konva';
import type Konva from 'konva';

/**
 * Props for VertexHandle component
 */
export interface VertexHandleProps {
  /** X position in pixels */
  x: number;
  /** Y position in pixels */
  y: number;
  /** Index of this vertex in the polygon */
  index: number;
  /** Whether this vertex is currently active (being dragged) */
  isActive?: boolean;
  /** Callback when drag starts */
  onDragStart?: (index: number) => void;
  /** Callback during drag with new position */
  onDragMove?: (index: number, position: { x: number; y: number }) => void;
  /** Callback when drag ends */
  onDragEnd?: (index: number) => void;
  /** Callback when vertex is clicked/tapped */
  onClick?: (index: number) => void;
  /** Handle radius in pixels (default: 8) */
  radius?: number;
  /** Whether the handle is draggable (default: true) */
  draggable?: boolean;
}

/** Default handle radius */
const DEFAULT_RADIUS = 8;

/** Colors for vertex handle */
const COLORS = {
  fill: '#ffffff',
  fillActive: '#3b82f6',
  fillHover: '#93c5fd',
  stroke: '#3b82f6',
  strokeActive: '#1d4ed8',
};

/**
 * VertexHandle Component
 *
 * Renders a draggable circle handle at a polygon vertex position.
 */
export const VertexHandle = ({
  x,
  y,
  index,
  isActive = false,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  radius = DEFAULT_RADIUS,
  draggable = true,
}: VertexHandleProps) => {
  const [isHovered, setIsHovered] = useState(false);

  /**
   * Get fill color based on state
   */
  const getFillColor = () => {
    if (isActive) return COLORS.fillActive;
    if (isHovered) return COLORS.fillHover;
    return COLORS.fill;
  };

  /**
   * Get stroke color based on state
   */
  const getStrokeColor = () => {
    if (isActive) return COLORS.strokeActive;
    return COLORS.stroke;
  };

  /**
   * Handle drag start event
   */
  const handleDragStart = useCallback(() => {
    onDragStart?.(index);
  }, [index, onDragStart]);

  /**
   * Handle drag move event
   */
  const handleDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      const node = e.target;
      onDragMove?.(index, { x: node.x(), y: node.y() });
    },
    [index, onDragMove]
  );

  /**
   * Handle drag end event
   */
  const handleDragEnd = useCallback(() => {
    onDragEnd?.(index);
  }, [index, onDragEnd]);

  /**
   * Handle click/tap event
   */
  const handleClick = useCallback(() => {
    onClick?.(index);
  }, [index, onClick]);

  /**
   * Handle mouse enter
   */
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  /**
   * Handle mouse leave
   */
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <Circle
      x={x}
      y={y}
      radius={radius}
      fill={getFillColor()}
      stroke={getStrokeColor()}
      strokeWidth={2}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onTap={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // Larger hit area for touch devices
      hitStrokeWidth={16}
      // Cursor style
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onMouseOver={(e: any) => {
        const container = e.target.getStage()?.container();
        if (container) {
          container.style.cursor = draggable ? 'grab' : 'pointer';
        }
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onMouseOut={(e: any) => {
        const container = e.target.getStage()?.container();
        if (container) {
          container.style.cursor = 'default';
        }
      }}
    />
  );
};

export default VertexHandle;
